import { Observable, defaultIfEmpty, forkJoin } from "rxjs";
import { AbstractControl } from '@angular/forms';
import { App_DisableNewConstraint, App_FormFieldCasesDynamiques, App_FormFieldCheckbox, App_FormFieldDrawing, App_FormFieldInputNumber, App_FormFieldInputText/*, App_FormFieldListUser*/, App_FormFieldListe, App_FormFieldRadio, App_FormFieldTextArea, App_Levage_Updateable, App_FormFieldMarqueurs, App_FormFieldImgUpload, App_FormSketchFormDynamique, App_FormSketchField } from "./interfaces";
import { Type_CustomField, Type_Field, Type_FieldOrLabel, Type_Label, Type_NoField } from "./types";
import { Enum_LevageStatus } from "./enums";

export const arrayIfNullOrUndefined = <T>(a: T[] | null | undefined) => a !== null && a !== undefined ? a : ([] as T[]);

export function instanceOfFieldLabel(object: Type_FieldOrLabel): object is Type_Label {
    return object.type === 'label';
}

export function instanceOfTrueField(object: Type_FieldOrLabel): object is Type_Field {
    return object.type === 'field';
}

export function instanceOfCustomField(object: Type_FieldOrLabel): object is Type_CustomField {
    return object.type === 'custom-field';
}

/** Même logique que ReadonlyByRulePipe : champ non saisissable pour cet état de levage. */
const READONLY_PREPARATION_ETATS = new Set<number>([1, 8]); // PREPARATION, ELABORATION_A_CORRIGER
const READONLY_REMPLISSAGE_ETATS = new Set<number>([2, 9]); // EN_COURS, ELABORATION_A_EXECUTER

export function readonlyRuleApplies(
    rule: number | null | undefined,
    etatId: number | null | undefined,
): boolean {
    const r = Number(rule ?? 0);
    const etat = Number(etatId ?? NaN);
    switch (r) {
        case 0: return false;
        case 1: return true;
        case 2: return Number.isFinite(etat) && READONLY_PREPARATION_ETATS.has(etat);
        case 3: return Number.isFinite(etat) && READONLY_REMPLISSAGE_ETATS.has(etat);
        default: return false;
    }
}

export function instanceOfFieldCheckbox(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldCheckbox {
    return object.subType === `checkbox`;
}

export function instanceOfFieldRadio(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldRadio {
    return object.subType === `radio`;
}

export function instanceOfFieldInputText(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldInputText {
    return object.subType === `input-text`;
}

export function instanceOfFieldInputNumber(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldInputNumber {
    return object.subType === `input-number`;
}

export function instanceOfFieldtextArea(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldTextArea {
    return object.subType === `textarea`;
}

export function instanceOfFieldListe(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldListe {
    return object.subType === `liste`;
}

/*
export function instanceOfFieldListUser(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldListUser {
    return object.subType === `list_user`;
}
*/

export function instanceOfFieldDrawing(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldDrawing {
    return object.subType === `drawing`;
}

export function instanceOfCasesDynamiques(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldCasesDynamiques {
    return object.subType === `case_dyn`;
}

export const evaluateInitialvalue = (field: Type_Field | App_FormSketchField | Type_NoField, dataLevage: App_Levage_Updateable | undefined) => {
    // TODO-pf? modifier recherche value
    const dataField = (dataLevage?.data?.datas || []).find(fieldInData => fieldInData.name === field.name)
    return dataField ? evaluateInitialValueWithData(dataField, field)
    : evaluateInitialValueNoData(field)
}

export const evaluateInitialValueDynamicForm = (field: App_FormSketchFormDynamique, prototypeOnAddMarkerInitialValue: any | undefined) => {
    return Array.isArray(prototypeOnAddMarkerInitialValue) ? prototypeOnAddMarkerInitialValue : [];
}

export function instanceOfFieldImgUpload(object: Type_Field | App_FormSketchField | Type_NoField): object is App_FormFieldImgUpload {
    return object.subType === `img-upload`;
}

export function instanceOfFieldMarqueurs(object: any): object is App_FormFieldMarqueurs {
    return object.subType === `markers`;
}

export const evaluateInitialValueWithData = (dataField: { name: string; value: any; id?: number}, field: Type_Field | App_FormSketchField | Type_NoField) => {
    return instanceOfFieldRadio(field) && field.multiple && typeof dataField.value === 'string' ? JSON.parse(dataField.value): dataField.value;
}

export const evaluateInitialValueNoData = (field: Type_Field | App_FormSketchField | Type_NoField) => {
    return instanceOfFieldCheckbox(field)
    ? false
    : instanceOfFieldInputText(field)
    ? ''
    : instanceOfFieldtextArea(field)
    ? ''
    : instanceOfFieldListe(field)
    ? null
    /*
    : instanceOfFieldListUser(field)
    ? null
    */
    : instanceOfFieldDrawing(field)
    ? ''
    : instanceOfCasesDynamiques(field)
    ? []
    : instanceOfFieldImgUpload(field)
    ? null
    : instanceOfFieldMarqueurs(field)
    ? null
    : instanceOfFieldRadio(field)
    ? field.multiple
        ? []
        : null
    : null
}

/**
 * fonction pour s'assurer qu'une collection d'observable en
 * forkJoin retourne un tableau vide si la collection l'est plutôt que never
 * @param obs un tableau d'observables
 * @returns un tableau vide si le tableau d'observables est vide
 */
export function securityForkJoin<T>(obs: Observable<T>[]): Observable<T[]> {
    return forkJoin(obs).pipe(defaultIfEmpty([]));
}

const getFileReader: () => FileReader = () => {
    const fileReader = new FileReader();
    const zoneOriginalInstance = (fileReader as any)[`__zone_symbol__originalInstance`];
    return zoneOriginalInstance || fileReader;
};

export const blobTo64: (blob: Blob) => Promise<string> = (blob: Blob) => new Promise(resolve => {
    const reader = getFileReader();
    reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
    };
    reader.readAsDataURL(blob);
});

export const isValidDate = (d: Date) => d instanceof Date && !Number.isNaN(d.getTime());

export const dirtyb64ToBlob: (b64Data: string, contentType: string) => Blob = (b64Data: string, contentType: string) => {
    contentType = contentType || '';
    const sliceSize = 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

export const removeFirstSlash = (str: string) => str.replace(/^\//, '');

export const statutText: (etat_id: number) => string = (etat_id: number) => {
    switch (etat_id) {

        case Enum_LevageStatus.PREPARATION:
            return 'En cours d\'élaboration';

        case Enum_LevageStatus.EN_COURS:
            return 'En cours';

        case Enum_LevageStatus.TERMINE:
            return 'Terminé';

        case Enum_LevageStatus.TERMINE_NON_REALISE:
            return 'Non réalisé';

        case Enum_LevageStatus.PREPARATION_EN_COURS_DE_VALIDATION:
            return 'À valider';

        case Enum_LevageStatus.PREPARATION_INVALIDE:
            return 'Élaboration invalide';

        case Enum_LevageStatus.PREPARATION_A_RECOMMENCER:
            return 'Élaboration à recommencer';
        
        case Enum_LevageStatus.ELABORATION_A_CORRIGER:
            return 'Élaboration à corriger';
            
        case Enum_LevageStatus.ELABORATION_A_EXECUTER:
            return 'Élaboration à executer';

        default:
            return '';
    }
}

export const extractExtensionFromWeb = (str: string) => {
    const regExp = /\.\w+$/;
    const match = str.match(regExp);
    if (match !== null) {
        return match[0];
    }
    return '';
}

export const extToMimeType = (key: string) => {
    switch (key) {
        case '.doc':
            return 'application/msword';
        case '.gif':
            return 'image/gif';
        case '.jpeg':
            return 'image/jpeg';
        case '.jpg':
            return ' image/jpeg';
        case '.png':
            return 'image/png';
        case '.pdf':
            return 'application/pdf';
        default:
            return ''
    }
}

/**
 * retourne un écart de temps entre deux dates selon la taille de l'écart en
 * - minutes
 * - puis heures
 * - puis jours
 * - puis semaines
 * - puis mois
 * @param v tuple de deux dates
 * @returns string représentant l'écart de temps entre les deux dates
 */
export function datesToEcartLibelle(v: [Date, Date]): string {
    const [currentDate, oldDate] = v;
    const compareMonth = new Date(currentDate);
    compareMonth.setMonth(compareMonth.getMonth() - 1);
    if (compareMonth.getTime() > oldDate.getTime()) {
        const ecart = (
            currentDate.getFullYear() - oldDate.getFullYear()) * 12 +
            currentDate.getMonth() - oldDate.getMonth()
        return `${ecart} mois`;
    } else {
        const forMinute = 60;
        const forHeure = forMinute * 60;
        const forJour = forHeure * 24;
        const forSemaine = forJour * 7;
        const ecart = Math.floor((currentDate.getTime() - oldDate.getTime()) / 1000)
        if (ecart >= forSemaine) {
            const semaine = Math.floor(ecart / forSemaine);
            return `${semaine} semaine${semaine > 1 ? 's' : ''}`;
        } else if (ecart >= forJour) {
            const jour = Math.floor(ecart / forJour);
            return `${jour} jour${jour > 1 ? 's' : ''}`;
        } else if (ecart >= forHeure) {
            const heure = Math.floor(ecart / forHeure);
            return `${heure} heure${heure > 1 ? 's' : ''}`;
        } else if (ecart >= forMinute) {
            const minute = Math.floor(ecart / forMinute);
            return `${minute} minute${minute > 1 ? 's' : ''}`;
        } else {
            const seconde = Math.floor(ecart);
            return 'moins d\'une minute';
        }
    }
}

/** Radio « choix multiple » (API : multiple === 1). */
export function isRadioMultipleField(field: Type_FieldOrLabel | undefined): boolean {
    if (!field || !instanceOfTrueField(field)) {
        return false;
    }
    return instanceOfFieldRadio(field) && field.multiple === 1;
}

/**
 * Jetons sélectionnés côté contrôle (tableau d'options, chaîne CSV ou JSON tableau).
 */
export function tokensFromMultiControlValue(raw: unknown): string[] {
    if (raw === null || raw === undefined) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw.map(v => String(v).trim()).filter(s => s.length > 0);
    }
    if (typeof raw === 'string') {
        const s = raw.trim();
        if (!s) {
            return [];
        }
        try {
            const parsed = JSON.parse(s) as unknown;
            if (Array.isArray(parsed)) {
                return parsed.map(v => String(v).trim()).filter(x => x.length > 0);
            }
        } catch {
            /* chaîne non-JSON */
        }
        if (s.includes(',')) {
            return s.split(',').map(t => t.trim()).filter(t => t.length > 0);
        }
        return [s];
    }
    return [String(raw)];
}

/** Une contrainte peut avoir value "1,3" → plusieurs jetons attendus. */
export function tokensFromConstraintExpectedValue(value: unknown): string[] {
    if (value === null || value === undefined) {
        return [];
    }
    const s = String(value).trim();
    if (!s) {
        return [];
    }
    if (s.includes(',')) {
        return s.split(',').map(t => t.trim()).filter(t => t.length > 0);
    }
    return [s];
}

export function evalDisableNewConstraintScalar(received: any, constraint: App_DisableNewConstraint): boolean {
    let expected: any = constraint.value;
    const operator = constraint.operator;

    if (constraint.type === 'number') {
        received = Number(received);
        expected = Number(expected);
    } else if (constraint.type === 'string') {
        received = String(received);
        expected = String(expected);
    }

    try {
        switch (operator) {
            case '==':
                return received == expected;
            case '!=':
                return received != expected;
            case '>':
                return received > expected;
            case '<':
                return received < expected;
            case '>=':
                return received >= expected;
            case '<=':
                return received <= expected;
            default:
                return false;
        }
    } catch {
        return false;
    }
}

/**
 * Évalue les contraintes disable_new, avec fusion « concat » pour un même champ radio multiple
 * quand l'expression n'a pas de || et qu'au moins deux contraintes == string ciblent ce champ :
 * la sélection doit être exactement cet ensemble de valeurs (même cardinalité, ordre indifférent).
 */
export function buildDisableNewConstraintResults(
    operation: string,
    constraints: App_DisableNewConstraint[],
    resolveControl: (champ: string) => AbstractControl | undefined,
    resolveFieldMeta: (champ: string) => Type_FieldOrLabel | undefined,
): { id: string; value: boolean }[] {
    const hasOr = operation.includes('||');
    const mergeable = constraints.filter(c =>
        c.operator === '==' &&
        c.type === 'string' &&
        isRadioMultipleField(resolveFieldMeta(c.champ)),
    );
    const byChamp = new Map<string, App_DisableNewConstraint[]>();
    for (const c of mergeable) {
        const list = byChamp.get(c.champ) ?? [];
        list.push(c);
        byChamp.set(c.champ, list);
    }
    const mergedBoolById = new Map<string, boolean>();
    if (!hasOr) {
        for (const [, list] of byChamp) {
            if (list.length < 2) {
                continue;
            }
            const ctrl = resolveControl(list[0].champ);
            if (!ctrl) {
                for (const c of list) {
                    mergedBoolById.set(c.id, false);
                }
                continue;
            }
            const expectedTokens: string[] = [];
            for (const c of list) {
                expectedTokens.push(...tokensFromConstraintExpectedValue(c.value));
            }
            const selectionTokens = tokensFromMultiControlValue(ctrl.value);
            // Égalité stricte d'ensemble : exactement ces valeurs, pas un sur-ensemble (ex. 1,3 ≠ 1,2,3).
            const mergedBool =
                expectedTokens.length > 0
                && expectedTokens.length === selectionTokens.length
                && expectedTokens.every((t: string) => selectionTokens.includes(t));
            for (const c of list) {
                mergedBoolById.set(c.id, mergedBool);
            }
        }
    }
    return constraints.map(constraint => {
        const merged = mergedBoolById.get(constraint.id);
        if (merged !== undefined) {
            return { id: constraint.id, value: merged };
        }
        const sourceField = resolveControl(constraint.champ);
        if (!sourceField) {
            return { id: constraint.id, value: false };
        }
        return {
            id: constraint.id,
            value: evalDisableNewConstraintScalar(sourceField.value, constraint),
        };
    });
}

